import { QueryObserver, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo } from "react";

import { fetchCardData } from "checkout-query-client/api/fetch-card-data";
import { ExperienceType } from "checkout-query-client/api/types";
import { CheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { useDoesSessionHaveSurcharge } from "checkout-query-client/hooks/use-does-session-have-surcharge";
import { PaymentType } from "checkout-query-client/types/form-types";

import { GetBinDataResponse } from "frontend-client";

export interface IsCreditCardProps {
  apiHost?: string;
  checkoutSessionToken?: string;
  cardNumber?: string;
  experienceType?: ExperienceType;
  encryptionKey?: string;
  checkoutSession?: CheckoutSession;
  target?: string;
  fieldIsValid?: boolean;
  merchantSurchargeFeeEnabled?: boolean;
  paymentType?: PaymentType;
  version: string;
}

type CreditCardQueryKey = Array<
  IsCreditCardProps & { scope: "card-type-for-surcharge" | "card-type" }
>;

export const useCreditCardObserver = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    return new QueryObserver<
      GetBinDataResponse,
      Error,
      GetBinDataResponse,
      { scope: string }
    >(queryClient, {
      queryKey: creditCardQueryKey,
      enabled: isCreditCardQueryEnabled,
    });
  }, [creditCardQueryKey]);
};

let creditCardQueryKey: CreditCardQueryKey = [
  { scope: "card-type-for-surcharge", version: "" },
];

let isCreditCardQueryEnabled = false;

export const useIsCreditCardWithSurcharge = ({
  apiHost,
  checkoutSessionToken,
  cardNumber,
  experienceType,
  encryptionKey,
  checkoutSession,
  target,
  fieldIsValid = false,
  merchantSurchargeFeeEnabled = false,
  paymentType,
  version,
}: IsCreditCardProps) => {
  const doesSessionHaveSurcharge = useDoesSessionHaveSurcharge({
    checkoutSession,
    target,
    merchantSurchargeFeeEnabled,
  });

  isCreditCardQueryEnabled =
    !!apiHost &&
    !!checkoutSessionToken &&
    !!cardNumber &&
    !!experienceType &&
    !!encryptionKey &&
    cardNumber !== "" &&
    doesSessionHaveSurcharge &&
    fieldIsValid &&
    paymentType === PaymentType.creditCard;

  // Debounce card number only once it is a valid field
  const debouncedCardNumber = useDebounce(
    cardNumber,
    isCreditCardQueryEnabled ? 500 : 0
  );

  creditCardQueryKey = useMemo(
    () =>
      [
        {
          scope: "card-type-for-surcharge",
          apiHost,
          checkoutSessionToken,
          cardNumber: debouncedCardNumber,
          experienceType,
          encryptionKey,
          version,
        },
      ] as CreditCardQueryKey,
    [
      apiHost,
      checkoutSessionToken,
      debouncedCardNumber,
      experienceType,
      encryptionKey,
      version,
    ]
  );

  return useQuery({
    queryKey: creditCardQueryKey,
    queryFn: fetchCardData,
    select: (data: GetBinDataResponse) => !data.isCardPinlessDebit,
    enabled: isCreditCardQueryEnabled,
    placeholderData: (prev: GetBinDataResponse | undefined) => prev,
    // Prevents HPP on continue triggering a refetch
    staleTime: Infinity,
  });
};
